// Optional, privacy-light play analytics (GoatCounter) + global error visibility. Tier-4 impure
// edge — never imported by the pure core (enforced by dependency-cruiser). Fully inert unless
// GOAT_CODE is set AND we're on a real host, so dev + e2e never phone home. GoatCounter is
// cookieless, so no consent banner is required. (BETA_PLAYBOOK §7, commit 2.)
import { GOAT_CODE } from './const';
import type { Game } from './game';

interface GoatCounter { count: (v: { path: string; title?: string; event?: boolean }) => void }
declare global { interface Window { goatcounter?: GoatCounter } }

let errorCount = 0; // cap error beacons per session so a crash loop can't spam

/** True only when analytics should actually run: configured, and not on a dev/test host. */
function enabled(): boolean {
  if (!GOAT_CODE) return false;
  const h = location.hostname;
  return h !== '' && h !== 'localhost' && !h.startsWith('127.');
}

/** Fire a custom event. No-op until the GoatCounter script has loaded (and forever when disabled). */
function ev(path: string): void {
  window.goatcounter?.count({ path, title: path, event: true });
}

/**
 * Register error visibility (always) and, when configured on a real host, load GoatCounter and
 * subscribe to the existing milestone/dungeon events. Safe to call unconditionally.
 */
export function initAnalytics(game: Game): void {
  // Error visibility: always log to the console; count *names only* (never messages/stacks — PII),
  // capped at 5/session. Complements the per-tick guard in main.ts (which stops white-screens).
  const onErr = (name: string) => { if (errorCount < 5) { errorCount++; ev('js-error/' + name); } };
  window.addEventListener('error', (e) => { console.error(e.error ?? e.message); onErr(e.error?.name ?? 'Error'); });
  window.addEventListener('unhandledrejection', (e) => { console.error(e.reason); onErr((e.reason && (e.reason as Error).name) || 'Rejection'); });

  if (!enabled()) return;

  // Inject the GoatCounter counter; it also auto-records the initial pageview.
  const s = document.createElement('script');
  s.async = true;
  s.src = '//gc.zgo.at/count.js';
  s.setAttribute('data-goatcounter', `https://${GOAT_CODE}.goatcounter.com/count`);
  document.head.appendChild(s);

  // Funnel — the milestone bus already fires the exact progression beats we care about
  // (firstWood, campfire, stoneTools, firstNightfall, firstDungeon, firstBoss, firstLegendary).
  game.on('milestone', (p) => ev('m/' + (p as { id: string }).id));
  game.on('dungeon-complete', (p) => {
    const d = p as { floor: number; cleared?: boolean };
    ev((d.cleared ? 'clear-f' : 'run-end-f') + d.floor);
  });

  // Return trigger: count once per day when this isn't the player's first-ever day.
  try {
    const today = new Date().toISOString().slice(0, 10);
    const first = localStorage.getItem('wg.firstSeen');
    if (!first) localStorage.setItem('wg.firstSeen', today);
    else if (first !== today && localStorage.getItem('wg.lastCountDay') !== today) ev('return');
    localStorage.setItem('wg.lastCountDay', today);
  } catch { /* private mode — skip */ }
}
